import type { LineStringGeometry, PolygonGeometry } from '@trafiku/contracts';

export type ShapeGeometry = LineStringGeometry | PolygonGeometry;
export type ShapeKind = 'LineString' | 'Polygon';

/**
 * Stored shapes as clean numeric 2D positions without repeated consecutive points (legacy data
 * sometimes has strings, a third dimension or duplicates); polygon rings closed. Null when unusable.
 */
export function cleanGeometry(g: ShapeGeometry | null | undefined): ShapeGeometry | null {
  if (!g || !Array.isArray(g.coordinates)) return null;
  const clean = (ring: unknown[]) => {
    const out: number[][] = [];
    for (const c of ring) {
      if (!Array.isArray(c)) continue;
      const p = [Number(c[0]), Number(c[1])];
      if (!p.every(Number.isFinite)) continue;
      const last = out.at(-1);
      if (!last || last[0] !== p[0] || last[1] !== p[1]) out.push(p);
    }
    return out;
  };
  if (g.type === 'LineString') {
    const coords = clean(g.coordinates as unknown[]);
    return coords.length >= 2 ? ({ type: 'LineString', coordinates: coords } as never) : null;
  }
  const rings = (g.coordinates as unknown[][]).map((ring) => {
    const r = clean(ring);
    const [first, last] = [r[0], r.at(-1)];
    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) r.push([...first]);
    return r;
  });
  return rings[0] && rings[0].length >= 4
    ? ({ type: 'Polygon', coordinates: rings.filter((r) => r.length >= 4) } as never)
    : null;
}

/** The shape as a GeoJSON Feature, for copying or downloading. */
export function shapeToText(g: ShapeGeometry | null | undefined, name: string): string {
  const geometry = cleanGeometry(g);
  return geometry ? JSON.stringify({ type: 'Feature', properties: { name }, geometry }, null, 2) : '';
}

type Parsed = { ok: true; geometry: ShapeGeometry } | { ok: false; error: string };
type Raw = { type?: string; coordinates?: unknown; geometry?: Raw; features?: Raw[] };

const lengthOf = (coords: unknown) => (Array.isArray(coords) ? coords.length : 0);

/** The geometry of the wanted kind inside any GeoJSON value (or a bare coordinate array). */
function pick(value: unknown, kind: ShapeKind): ShapeGeometry | null {
  if (Array.isArray(value)) {
    // Bare coordinates: [[lon, lat], …] for a route, [[[lon, lat], …]] for an area.
    const depth = Array.isArray(value[0]) ? (Array.isArray(value[0][0]) ? 3 : 2) : 1;
    if (kind === 'LineString' && depth === 2) return { type: 'LineString', coordinates: value } as never;
    if (kind === 'Polygon' && depth === 3) return { type: 'Polygon', coordinates: value } as never;
    return null;
  }
  const v = (value ?? {}) as Raw;
  if (v.type === 'FeatureCollection') {
    for (const f of v.features ?? []) {
      const g = pick(f, kind);
      if (g) return g;
    }
    return null;
  }
  if (v.type === 'Feature') return pick(v.geometry, kind);
  if (v.type === kind) return { type: kind, coordinates: v.coordinates } as never;
  // Multi-part shapes: the longest route, or the largest outline.
  if (kind === 'LineString' && v.type === 'MultiLineString' && Array.isArray(v.coordinates)) {
    const longest = [...v.coordinates].sort((a, b) => lengthOf(b) - lengthOf(a))[0];
    return longest ? ({ type: 'LineString', coordinates: longest } as never) : null;
  }
  if (kind === 'Polygon' && v.type === 'MultiPolygon' && Array.isArray(v.coordinates)) {
    const largest = [...v.coordinates].sort((a, b) => lengthOf(b?.[0]) - lengthOf(a?.[0]))[0];
    return largest ? ({ type: 'Polygon', coordinates: largest } as never) : null;
  }
  return null;
}

/**
 * Reads pasted or imported GeoJSON (a geometry, a Feature, a FeatureCollection or bare coordinates)
 * as the shape the editor expects. Errors are English keys, translated by the caller.
 */
export function parseShape(text: string, kind: ShapeKind): Parsed {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, error: 'This is not valid JSON.' };
  }
  const picked = pick(value, kind);
  if (!picked)
    return {
      ok: false,
      error: kind === 'Polygon' ? 'No area (Polygon) found in it.' : 'No route (LineString) found in it.',
    };
  const geometry = cleanGeometry(picked);
  if (!geometry)
    return {
      ok: false,
      error:
        kind === 'Polygon'
          ? 'An area needs at least three distinct points.'
          : 'A route needs at least two distinct points.',
    };
  const points = (
    geometry.type === 'LineString' ? geometry.coordinates : geometry.coordinates[0]
  ) as number[][];
  if (points.some(([lon, lat]) => Math.abs(lon!) > 180 || Math.abs(lat!) > 90))
    return { ok: false, error: 'Some coordinates are outside the map.' };
  // GeoJSON is [longitude, latitude]; Kosovo is around [21, 42.6]. Swapped pairs land near [42, 21].
  if (points.every(([lon, lat]) => lon! > 40 && lon! < 44 && lat! > 19 && lat! < 22))
    return { ok: false, error: 'The coordinates look swapped: GeoJSON uses [longitude, latitude].' };
  return { ok: true, geometry };
}
